import { Fragment, useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { oneLine } from 'common-tags';

import { Hamburger } from '@/atoms';

import { useRouteChangeStart } from '@/hooks';
import { routes } from '@/lib';
import { DesktopNav } from './desktop-nav/DesktopNav';
import { MobileNav } from './mobile-nav/MobileNav';

import type { FunctionComponent } from 'react';
import type { TypeHeaderProps } from './types';

/**
 * Header
 */
const SCROLL_THRESHOLD = 50;

export const Header: FunctionComponent<TypeHeaderProps> = ({
  className = '',
  theme = 'light',
  logo,
  logoScrolled,
  menu,
  socials,
  topBar,
  menuLayout = 'classic',
  transparent = false,
  sticky = false,
}: TypeHeaderProps) => {
  const [isOpen, setOpen] = useState(false);
  const [isOpenMobile, setOpenMobile] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useRouteChangeStart(() => setOpen(false));

  // Cuando es transparente, vigilamos el scroll para que al pasar el threshold
  // el header se vea sólido (bg + shadow + texto/logo oscuros).
  useEffect(() => {
    if (!transparent) return;
    const onScroll = () => setScrolled(window.scrollY > SCROLL_THRESHOLD);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [transparent]);

  const isTransparentVisual = transparent && !scrolled && !isOpenMobile;
  // Drawer mobile abierto → mostrar logo "scrolled" (oscuro), igual que al
  // scrollear: el header pasa a fondo blanco y necesita un logo legible.
  const activeLogo =
    (transparent && scrolled && logoScrolled) || (isOpenMobile && logoScrolled)
      ? logoScrolled!
      : logo;
  // Cuando el header está en estado transparente visual, los enlaces y el
  // resto del nav se renderizan como si la página tuviera theme oscuro
  // (texto blanco para contrastar con el hero). Al scrollear o abrir el
  // drawer mobile, recuperamos el theme real de la página.
  const effectiveTheme = isTransparentVisual ? 'dark' : theme;

  const isCentered = menuLayout === 'centered';
  const splitIndex = Math.ceil(menu.length / 2);
  const leftMenu = isCentered ? menu.slice(0, splitIndex) : [];
  const rightMenu = isCentered ? menu.slice(splitIndex) : [];

  const logoImage = (
    <Image
      width={activeLogo.width}
      height={activeLogo.height}
      src={activeLogo.src}
      alt={activeLogo.alt || 'Logo de la web'}
      priority
    />
  );

  return (
    <header
      // Línea inferior: token `--tw-color-header-border` (campo `headerBorderColor`),
      // emitido sólo si el tenant lo define; sin él cae al neutrals-100 histórico.
      className={oneLine`lg:[&:has([data-menu]:hover)]:text-text-primary lg:[&:has([data-menu]:hover)]:bg-neutrals-0 h-(--header-height) transition-colors duration-200 lg:h-auto [&:has([data-submenu-open="true"])_[data-button]]:hidden ${
        transparent
          ? sticky
            ? 'fixed top-0 right-0 left-0 z-40'
            : 'absolute top-0 right-0 left-0 z-40'
          : sticky
            ? 'border-[rgb(var(--tw-color-header-border,var(--tw-color-neutrals-100)))] sticky top-0 right-0 left-0 z-40 border-b bg-[var(--page-bgcolor,#fff)]'
            : 'border-[rgb(var(--tw-color-header-border,var(--tw-color-neutrals-100)))] border-b bg-[var(--page-bgcolor,#fff)]'
      } ${
        transparent && scrolled
          ? 'border-[rgb(var(--tw-color-header-border,var(--tw-color-neutrals-100)))] border-b bg-[var(--page-bgcolor,#fff)]'
          : ''
      } ${
        isOpen || isOpenMobile
          ? 'text-text-primary border-secondary-200 bg-neutrals-0 fixed! top-0 right-0 left-0 z-40 border-b lg:relative! lg:border-none lg:bg-transparent'
          : effectiveTheme === 'dark'
            ? 'text-neutrals-0'
            : 'text-text-primary'
      } ${className} `}
    >
      {topBar && topBar.length > 0 && (
        <div className="bg-primary-900">
          <div className="mx-auto hidden w-full max-w-(--wrapper-size) flex-row items-center justify-end gap-4 px-6 py-1.5 lg:flex">
            {topBar.map((child, index) => (
              <Fragment key={index}>
                {index !== 0 && (
                  <hr className="bg-neutrals-0 text-neutrals-0 my-px h-5 w-px" />
                )}
                <Link
                  className="text-body-m text-neutrals-0 font-medium"
                  href={child.url}
                >
                  {child.title}
                </Link>
              </Fragment>
            ))}
          </div>
        </div>
      )}

      {isCentered ? (
        <>
          {/* Mobile (Figma HoM): hamburger izquierda · logo centrado · "Contacto" derecha.
              Grid de 3 columnas para que el logo quede REALMENTE centrado pese a
              que hamburger y el link de la derecha tengan anchos distintos.
              `relative z-30` para quedar por encima del drawer (z-20) cuando
              abre — si no, el `<ul>` del drawer dentro del `<header>` tapa
              al trigger pese al z-40 del header (stacking context interno). */}
          <div className="relative z-30 grid h-(--header-height) w-full grid-cols-3 items-center px-4 lg:hidden">
            <div className="justify-self-start">
              <Hamburger
                theme={effectiveTheme}
                isOpen={isOpenMobile}
                onClick={() => setOpenMobile(!isOpenMobile)}
                aria-expanded={isOpenMobile}
                aria-controls="main-menu"
                aria-label="Abrir menú principal"
              />
            </div>

            <Link
              href={routes.index}
              className="relative z-20 shrink-0 justify-self-center"
              aria-label="Inicio"
            >
              {logoImage}
            </Link>

            <Link
              href={routes.contact}
              // Sobre el hero (header transparente, no scrolleado, drawer
              // cerrado) → blanco para contrastar con la imagen. En cualquier
              // otro estado (drawer mobile abierto = fondo blanco, página
              // scrolleada con header sólido, o página sin transparencia)
              // → color de marca para no quedar invisible.
              className={`justify-self-end text-sm font-bold whitespace-nowrap ${
                isTransparentVisual ? 'text-neutrals-0' : 'text-primary-500'
              }`}
            >
              Contacto
            </Link>
          </div>

          {/* Desktop (lg+): leftMenu | logo | rightMenu */}
          <div className="mx-auto hidden h-(--header-height) w-full max-w-(--wrapper-size) flex-row items-center justify-between gap-6 px-(--header-px) lg:flex">
            <DesktopNav menu={leftMenu} theme={effectiveTheme} />

            <Link
              href={routes.index}
              className="relative z-20 shrink-0"
              aria-label="Inicio"
            >
              {logoImage}
            </Link>

            <DesktopNav menu={rightMenu} theme={effectiveTheme} />
          </div>

          <MobileNav
            topBar={topBar}
            open={isOpenMobile}
            setOpen={setOpenMobile}
            menu={menu}
            socials={socials}
            theme={effectiveTheme}
            hideTrigger
          />
        </>
      ) : (
        <div className="mx-auto flex h-(--header-height) w-full max-w-(--wrapper-size) flex-row items-center justify-between lg:px-4">
          <Link
            href={routes.index}
            className={
              isOpenMobile ? 'hidden' : 'relative z-20 w-full pl-4 lg:pl-0'
            }
          >
            {logoImage}
          </Link>

          <div className="flex h-full w-full items-center justify-end pb-px">
            <DesktopNav menu={menu} theme={effectiveTheme} />

            <MobileNav
              topBar={topBar}
              open={isOpenMobile}
              setOpen={setOpenMobile}
              menu={menu}
              socials={socials}
              theme={theme}
            />
          </div>
        </div>
      )}
    </header>
  );
};

Header.displayName = 'Header';
